// "Sync now" (2026-10-29): fetch new StatSports sessions from the Session Editor with the existing Phase 2 sync (one job
// on the existing queue and runner; nothing else). Default: yesterday and today (Amsterdam), without force, so complete
// raw data is skipped and periods are never touched (the sync writes only statsports_* tables). One job at a time: the
// server refuses a second one while a sync is queued or running (409), and the button then shows that job's progress.
// Shows counters only, never a player name.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getStatsportsJobs, getStatsportsJob, startStatsportsSync } from '../../../api';
import { clock, toMs } from './seShared';

const ACTIVE = new Set(['queued', 'running']);
const FINISHED = new Set(['done', 'failed', 'cancelled']);
const MAX_DAYS = 7;
const dateFmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam', year: 'numeric', month: '2-digit', day: '2-digit' });
export const amsterdamDate = (offsetDays = 0, now = Date.now()) => dateFmt.format(new Date(now + offsetDays * 86400000));
const days = (from, to) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000) + 1;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const range = j => (j.dateFrom === j.dateTo ? j.dateFrom : `${j.dateFrom} – ${j.dateTo}`);

// While running: where it is and what it has done so far
export function syncProgressText(job) {
  const p = job.progress || {};
  if (job.status === 'queued') return `waiting to start (${range(job)})`;
  const where = [p.current_date ? `date ${p.current_date}` : null, p.position ? `session ${p.position.session}, player ${p.position.player}` : null].filter(Boolean).join(' · ');
  return `syncing ${where || range(job)} · so far ${plural(p.sessions_seen || 0, 'session')} (${p.sessions_new || 0} new), ${plural(p.players_seen || 0, 'player')}, ${plural(p.drills_seen || 0, 'drill')}`;
}
// When finished: one line. A date with no sessions (StatSports answers 204) is a normal "no new sessions found".
export function syncResultText(job) {
  const p = job.progress || {};
  if (job.status === 'failed') return `sync failed: ${job.error || 'the sync stopped'}`;
  if (job.status === 'cancelled') return 'sync cancelled';
  const changed = (p.drills_window_changed || 0) + (p.drills_kpi_changed || 0);
  const extra = p.raw_failed ? `; GPS download failed for ${plural(p.raw_failed, 'player')}` : '';
  if (!p.sessions_new && !p.drills_new && !changed) {
    return `no new sessions found${p.sessions_seen ? ` (${plural(p.sessions_seen, 'session')} already up to date)` : ''}${extra}`;
  }
  return `${plural(p.sessions_new || 0, 'new session')}, ${plural(p.drills_seen || 0, 'drill')}, ${plural(changed, 'changed drill')}${extra}`;
}

export default function SyncNow({ token, onSynced, pollMs = 2000 }) {
  const [active, setActive] = useState(null);
  const [last, setLast] = useState(null);
  const [menu, setMenu] = useState(false);
  const [from, setFrom] = useState(amsterdamDate(-1));
  const [to, setTo] = useState(amsterdamDate(0));
  const [forceDate, setForceDate] = useState(amsterdamDate(0));
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const watching = useRef(null);   // the job this page started or picked up: reported to onSynced when it finishes

  const load = useCallback(() => getStatsportsJobs(token, 10).then(r => {
    const jobs = r.jobs || [];
    const run = jobs.find(j => ACTIVE.has(j.status)) || null;
    setActive(run); if (run) watching.current = run.id;
    setLast(jobs.find(j => FINISHED.has(j.status)) || null);
  }).catch(() => {}), [token]);
  useEffect(() => { load(); }, [load]);

  // follow the active job until it finishes
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => {
      getStatsportsJob(token, active.id).then(r => {
        const j = r.job;
        if (ACTIVE.has(j.status)) { setActive(j); return; }
        setActive(null); setLast(j); setMsg('');
        if (watching.current === j.id) { watching.current = null; if (onSynced) onSynced(j); }
      }).catch(() => {});
    }, pollMs);
    return () => clearInterval(t);
  }, [active && active.id, token, pollMs, onSynced]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = (data, label) => {
    setBusy(true); setMsg('');
    startStatsportsSync(token, data).then(r => { watching.current = r.job.id; setActive(r.job); setMenu(false); })
      .catch(e => { if (e.status === 409) { setMsg('a sync is already running: showing its progress'); load(); } else setMsg(`could not start the sync (${label}): ${e.message}`); })
      .finally(() => setBusy(false));
  };
  const syncNow = () => start({ from: amsterdamDate(-1), to: amsterdamDate(0), force: false }, 'yesterday and today');
  const syncRange = () => {
    if (!from || !to || from > to) { setMsg('choose a start date on or before the end date'); return; }
    if (days(from, to) > MAX_DAYS) { setMsg(`at most ${MAX_DAYS} days at a time`); return; }
    start({ from, to, force: false }, range({ dateFrom: from, dateTo: to }));
  };
  const forceSync = () => {
    if (!forceDate) return;
    const ok = window.confirm(`Force re-sync ${forceDate}?\n\nThis downloads the raw GPS data of that date again and updates only the imported Sonra data `
      + '(sessions, drills and raw samples). Your own periods, notes and hints are not changed. It takes longer than a normal sync.');
    if (ok) start({ from: forceDate, to: forceDate, force: true }, `force ${forceDate}`);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
      <div className="se-grp" style={{ gap: 6 }}>
        <button className="se-btn se-btn--primary" disabled={!!active || busy} onClick={syncNow}
          title="Fetch new StatSports sessions of yesterday and today (completed raw data is skipped; your periods are never touched)">
          {active ? 'Syncing…' : 'Sync now'}
        </button>
        <button className="se-btn" aria-label="More sync options" aria-expanded={menu} disabled={!!active} onClick={() => setMenu(m => !m)}>⋯</button>
        <span className="se-muted" data-testid="sync-status">
          {active ? syncProgressText(active)
            : last ? `last synced ${clock(toMs(last.finishedAt || last.heartbeatAt))} (${range(last)}${last.force ? ', force' : ''}): ${syncResultText(last)}` : 'not synced from here yet'}
        </span>
      </div>
      {msg && <span className="se-muted" style={{ color: 'var(--se-warn)' }}>{msg}</span>}
      {menu && (
        <div className="se-sub" style={{ position: 'absolute', top: '100%', left: 0, zIndex: 5, background: 'var(--bg-card)', minWidth: 420 }}>
          <strong>Sync a specific date or range</strong> <span className="se-muted">(at most {MAX_DAYS} days, one date at a time)</span>
          <div className="se-bar" style={{ marginTop: 6 }}>
            <input className="se-input" type="date" aria-label="Sync from" value={from} onChange={e => setFrom(e.target.value)} />
            <span className="se-muted">to</span>
            <input className="se-input" type="date" aria-label="Sync to" value={to} onChange={e => setTo(e.target.value)} />
            <button className="se-btn" disabled={busy} onClick={syncRange}>Sync</button>
          </div>
          <div style={{ marginTop: 10 }}><strong>Force re-sync this date</strong> <span className="se-muted">(downloads the raw data again; only imported data changes)</span></div>
          <div className="se-bar" style={{ marginTop: 6 }}>
            <input className="se-input" type="date" aria-label="Force re-sync date" value={forceDate} onChange={e => setForceDate(e.target.value)} />
            <button className="se-btn" disabled={busy} onClick={forceSync}>Force re-sync…</button>
          </div>
        </div>
      )}
      <span className="se-muted" style={{ fontSize: 11 }}>
        StatSports only has what Sonra has already processed: if a session you just downloaded is not found, wait a few minutes in Sonra and press Sync now again.
      </span>
    </div>
  );
}
